// Forge task queue: fetching, opening, completing, skipping, assigning and notes.
import {
  FetchForgeTasksParams,
  ForgeTask,
  ForgeTaskCompleteRequest,
  ForgeTaskDetailResponse,
  ForgeTaskDetectionsResponse,
  ForgeTaskNote,
  ForgeTaskNoteCreateRequest,
  ForgeTaskNotesResponse,
  ForgeTaskNoteTags,
  ForgeTaskNoteTagsUpdateRequest,
  ForgeTaskOpenResponse,
  ForgeTasksAssignRequest,
  ForgeTasksAssignResponse,
  ForgeTaskSavesResponse,
  ForgeTaskSkipRequest,
  ForgeTasksRequestMoreRequest,
  ForgeTasksRequestMoreResponse,
  ForgeTasksResponse,
  ForgeTaskTool,
  ForgeTeamAssigneesResponse,
} from '../../types/tasks';
import { forgeRequest } from '../forgeRequest';
import { FORGE_ADMIN_API_URL } from './endpoints';

export async function fetchForgeTasks<TTool extends ForgeTaskTool>(
  token: string,
  params: FetchForgeTasksParams<TTool>
): Promise<ForgeTasksResponse<TTool>> {
  const query = new URLSearchParams({ tool: params.tool });
  if (params.chainId !== undefined && params.chainId !== null) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.view !== undefined) query.set('view', params.view);
  if (params.finishedFrom) query.set('finished_from', params.finishedFrom);
  if (params.finishedTo) query.set('finished_to', params.finishedTo);
  params.assignee?.forEach((email) => query.append('assignee', email));
  params.claimant?.forEach((email) => query.append('claimant', email));
  if (params.modTagPrefix) query.set('mod_tag_prefix', params.modTagPrefix);
  if (params.targetKey) query.set('target_key', params.targetKey);
  params.cameraIds?.forEach((cameraId) => query.append('camera_id', String(cameraId)));
  if (params.taskListId) query.set('task_list_id', params.taskListId);
  if (params.sort) {
    query.set('sort', params.sort);
    if (params.sortDirection) query.set('sort_direction', params.sortDirection);
  }
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  return forgeRequest<ForgeTasksResponse<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks?${query.toString()}`,
    'Error fetching Forge tasks'
  );
}

/** The task list's export page size; with a later page the bar shows the openable count and "+". */
const ASSIGNED_FORGE_TASKS_LIMIT = 500;

/** The live tasks assigned to one labeler on every Chain, in task list order (DLI-2210). */
export async function fetchAssignedForgeTasks<TTool extends ForgeTaskTool>(
  token: string,
  params: { tool: TTool; assigneeEmail: string }
): Promise<ForgeTasksResponse<TTool>> {
  return fetchForgeTasks(token, {
    tool: params.tool,
    view: 'live',
    assignee: [params.assigneeEmail],
    limit: ASSIGNED_FORGE_TASKS_LIMIT,
  });
}

export async function fetchForgeTask<TTool extends ForgeTaskTool = 'remote_dc'>(
  token: string,
  taskId: string,
  options?: { signal?: AbortSignal }
): Promise<ForgeTaskDetailResponse<TTool>> {
  return forgeRequest<ForgeTaskDetailResponse<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}`,
    'Error fetching Forge task',
    { signal: options?.signal }
  );
}

/** Scores every detection on a Remote DC task's image, so thresholds wider than the task's find more. */
export async function fetchForgeTaskDetections(
  token: string,
  taskId: string,
  params: {
    similarityThreshold?: number;
    deltaThreshold?: number;
    ssmConfidenceThreshold?: number;
    lowEmbeddingThreshold?: number | null;
    signal?: AbortSignal;
  } = {}
): Promise<ForgeTaskDetectionsResponse> {
  const query = new URLSearchParams();
  if (params.similarityThreshold !== undefined) {
    query.set('similarity_threshold', String(params.similarityThreshold));
  }
  if (params.deltaThreshold !== undefined) {
    query.set('delta_threshold', String(params.deltaThreshold));
  }
  if (params.ssmConfidenceThreshold !== undefined) {
    query.set('ssm_confidence_threshold', String(params.ssmConfidenceThreshold));
  }
  if (params.lowEmbeddingThreshold !== undefined && params.lowEmbeddingThreshold !== null) {
    query.set('low_embedding_threshold', String(params.lowEmbeddingThreshold));
  }
  const queryString = query.toString();
  return forgeRequest<ForgeTaskDetectionsResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/detections${
      queryString ? `?${queryString}` : ''
    }`,
    'Error fetching Forge task detections',
    { signal: params.signal }
  );
}

export async function openForgeTask<TTool extends ForgeTaskTool = 'remote_dc'>(
  token: string,
  taskId: string
): Promise<ForgeTaskOpenResponse<TTool>> {
  return forgeRequest<ForgeTaskOpenResponse<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/open`,
    'Error opening Forge task',
    { method: 'POST' }
  );
}

export async function fetchForgeTaskSaves(
  token: string,
  taskId: string,
  options?: { signal?: AbortSignal; includeSnapshots?: boolean }
): Promise<ForgeTaskSavesResponse> {
  // Without it every save pictures only the crops it changed; the expanded change details ask for
  // the full before/after galleries.
  const query = options?.includeSnapshots ? '?include_snapshots=true' : '';
  return forgeRequest<ForgeTaskSavesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/saves${query}`,
    'Error fetching Forge task saves',
    { signal: options?.signal }
  );
}

export async function fetchForgeTaskNotes(
  token: string,
  taskId: string
): Promise<ForgeTaskNotesResponse> {
  return forgeRequest<ForgeTaskNotesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/notes`,
    'Error fetching Forge task notes'
  );
}

export async function createForgeTaskNote(
  token: string,
  taskId: string,
  request: ForgeTaskNoteCreateRequest
): Promise<ForgeTaskNote> {
  return forgeRequest<ForgeTaskNote>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/notes`,
    'Error adding Forge task note',
    { method: 'POST', body: request }
  );
}

export async function fetchForgeTaskNoteTags(
  token: string,
  tool: ForgeTaskTool
): Promise<ForgeTaskNoteTags> {
  const query = new URLSearchParams({ tool });
  return forgeRequest<ForgeTaskNoteTags>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/note-tags?${query.toString()}`,
    'Error fetching Forge task note tags'
  );
}

export async function updateForgeTaskNoteTags(
  token: string,
  tool: ForgeTaskTool,
  request: ForgeTaskNoteTagsUpdateRequest
): Promise<ForgeTaskNoteTags> {
  const query = new URLSearchParams({ tool });
  return forgeRequest<ForgeTaskNoteTags>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/note-tags?${query.toString()}`,
    'Error updating Forge task note tags',
    { method: 'PUT', body: request }
  );
}

export async function completeForgeTask<TTool extends ForgeTaskTool = 'remote_dc'>(
  token: string,
  taskId: string,
  request?: ForgeTaskCompleteRequest
): Promise<ForgeTask<TTool>> {
  return forgeRequest<ForgeTask<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/done`,
    'Error completing Forge task',
    // The endpoint still finishes Remote DC tasks with no body; only Cleaning reports an outcome.
    request ? { method: 'POST', body: request } : { method: 'POST' }
  );
}

export async function skipForgeTask<TTool extends ForgeTaskTool = 'remote_dc'>(
  token: string,
  taskId: string,
  request: ForgeTaskSkipRequest
): Promise<ForgeTask<TTool>> {
  return forgeRequest<ForgeTask<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/${encodeURIComponent(taskId)}/skip`,
    'Error skipping Forge task',
    { method: 'POST', body: request }
  );
}

export async function assignForgeTasks<TTool extends ForgeTaskTool = 'remote_dc'>(
  token: string,
  request: ForgeTasksAssignRequest
): Promise<ForgeTasksAssignResponse<TTool>> {
  return forgeRequest<ForgeTasksAssignResponse<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/assign`,
    'Error assigning Forge tasks',
    { method: 'POST', body: request }
  );
}

/** Assign the caller the chain's next unassigned tasks. Self-service: the caller is the assignee. */
export async function requestMoreForgeTasks<TTool extends ForgeTaskTool = 'remote_dc'>(
  token: string,
  request: ForgeTasksRequestMoreRequest
): Promise<ForgeTasksRequestMoreResponse<TTool>> {
  return forgeRequest<ForgeTasksRequestMoreResponse<TTool>>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/request-more`,
    'Error requesting more Forge tasks',
    { method: 'POST', body: request }
  );
}

/** The shared team assignee list, one for the whole team. */
export async function fetchForgeTeamAssignees(token: string): Promise<ForgeTeamAssigneesResponse> {
  return forgeRequest<ForgeTeamAssigneesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/team-assignees`,
    'Error fetching the team assignee list'
  );
}

export async function addForgeTeamAssignee(
  token: string,
  email: string
): Promise<ForgeTeamAssigneesResponse> {
  return forgeRequest<ForgeTeamAssigneesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/team-assignees`,
    'Error adding to the team assignee list',
    { method: 'POST', body: { email } }
  );
}

export async function removeForgeTeamAssignee(
  token: string,
  email: string
): Promise<ForgeTeamAssigneesResponse> {
  return forgeRequest<ForgeTeamAssigneesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/tasks/team-assignees/${encodeURIComponent(email)}`,
    'Error removing from the team assignee list',
    { method: 'DELETE' }
  );
}
